/**
 * PlanningPage — Financial Independence / FIRE planner
 *
 * Answers the only question that matters: "Have we hit FIRE?"
 *
 * Lets you adjust the assumptions (annual spend, SWR, age, savings, real
 * return) and immediately see classification, multiple, surplus/shortfall,
 * years-to-FIRE, and a 50-year withdrawal projection chart.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Target, TrendingUp, AlertTriangle, Calendar, Sparkles, RefreshCw,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, ComposedChart, ReferenceLine,
} from 'recharts'
import { api, updateHouseholdProfile } from '@/lib/api'
import { usePlanningAssumptions } from '@/hooks/usePlanningAssumptions'
import ScenarioDisclaimerFooter from '@/components/ScenarioDisclaimerFooter'
import EditableNumberSlider from '@/components/ui/EditableNumberSlider'
import { fmtGBP, fmtGBPCompact, fmtNum, fmtPct } from '@/lib/format'
import { gridStroke, axisTickFill, tooltipContentStyle } from '@/lib/chartTheme'

interface FireStatus {
  headline: string
  tracked_total_gbp: number
  fire_applicable_gbp: number
  illiquid_assets_gbp: number
  property_value_gbp: number
  other_assets_gbp: number
  total_net_worth_gbp: number
  household_total_gbp: number
  productive_total_gbp: number
  annual_spend_gbp: number
  swr_pct: number
  target_required_gbp: number
  target_with_state_pension_gbp: number
  shortfall_gbp: number
  surplus_gbp: number
  shortfall_today_gbp?: number
  surplus_today_gbp?: number
  retirement_ready?: boolean
  property_at_retirement_gbp?: number
  property_net_equity_gbp?: number
  property_fire_contribution_gbp?: number
  current_multiple: number
  years_to_fire: number | null
  years_to_fire_with_state_pension: number | null
  fire_age: number | null
  fire_age_with_state_pension: number | null
  classification: { tier: string; label: string; multiple: number; achieved: boolean }
  multiples_table: {
    swr_pct: number
    required_gbp: number
    achieved: boolean
    shortfall_gbp: number
    surplus_gbp: number
  }[]
  value_at_retirement_gbp: number
  sequence_risk: { level: string; post_crash_value_gbp: number; post_crash_swr_pct: number; explanation: string }
  projection: {
    year: number
    value: number
    property_value?: number
    total_net_worth?: number
    withdrawal: number
    state_pension: number
    net_drawdown: number
    contribution: number
    property_injection?: number
    phase?: 'working' | 'retired'
  }[]
  assumptions: Record<string, any>
}

const TIER_COLOURS: Record<string, string> = {
  'FAT FIRE':       'text-emerald bg-emerald/10 border-emerald/40',
  'STANDARD FIRE':  'text-emerald-light bg-emerald/10 border-emerald/30',
  'LEAN FIRE':      'text-blue-light bg-blue/10 border-blue/30',
  'BARISTA FIRE':   'text-amber bg-amber/10 border-amber/30',
  'COAST FIRE':     'text-amber-light bg-amber/10 border-amber/30',
  'ACCUMULATING':   'text-rose-light bg-rose/10 border-rose/30',
}

// One plain-English line per tier, in household terms (shown under the badge).
const TIER_EXPLAINERS: Record<string, string> = {
  'FAT FIRE':      'More than enough — the pot funds your lifestyle at a cautious 3% withdrawal rate with a big safety margin.',
  'STANDARD FIRE': 'Work is optional — the pot covers your spending at a conservative 3.5% withdrawal rate.',
  'LEAN FIRE':     'You could retire now on the classic 4% rule, though with little slack for surprises.',
  'BARISTA FIRE':  'Nearly there — the pot covers most of your spending; light part-time work bridges the rest.',
  'COAST FIRE':    'Enough saved already — growth alone gets you there by retirement age; new savings optional.',
  'ACCUMULATING':  'Still building — keep saving; the projection below shows when the pot gets you there.',
}

export default function PlanningPage() {
  // Inputs
  const [spend, setSpend] = useState(220_000)
  const [swr, setSwr] = useState(3.5)
  const [age, setAge] = useState(57)
  const [retireAge, setRetireAge] = useState(60)
  const [savings, setSavings] = useState(40_000)   // default: Simon + Unman fill both ISAs
  const [realReturn, setRealReturn] = useState(5.0)
  const [includeSP, setIncludeSP] = useState(true)
  const [externalCash, setExternalCash] = useState(0)
  const [propertyValue, setPropertyValue] = useState(0)
  const [otherAssets, setOtherAssets] = useState(0)
  const [propertyContributionPct, setPropertyContributionPct] = useState(0)
  const [propertyRealGrowth, setPropertyRealGrowth] = useState(1.0)
  // Property sell scenario: stored as years-from-now so it's independent
  // of absolute age. Backend takes sell_property_at_age so we add the two.
  const [sellPropertyInYears, setSellPropertyInYears] = useState<number | ''>('')
  const [sellPropertyPct, setSellPropertyPct] = useState(100)

  // ── Farm Avenue property sale scenario ──────────────────────────────
  const [sellFarmAvenue, setSellFarmAvenue] = useState(true)
  const [farmAvenueSaleYear, setFarmAvenueSaleYear] = useState(2026)
  const [farmAvenueNetProceeds, setFarmAvenueNetProceeds] = useState(310_770)

  const [data, setData] = useState<FireStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debounced write-back to household profile (500 ms).
  // Used for the legacy planning_assumptions sub-dict (Farm Avenue toggles
  // etc.) — those still write to PUT /api/profile until the per-property
  // sliders migrate to the canonical `property_plans` list.
  const profileUpdateTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debouncedProfileUpdate = useCallback((updates: Record<string, number>) => {
    if (profileUpdateTimer.current) clearTimeout(profileUpdateTimer.current)
    profileUpdateTimer.current = setTimeout(() => {
      updateHouseholdProfile(updates).catch(() => {})
    }, 500)
  }, [])

  // Phase A — shared planning store. Sliders for the canonical numeric
  // assumptions (spend / ages / savings / SWR / return) write through the
  // PATCH /api/profile/assumptions endpoint so MC / drawdown / scenarios
  // see the change next tick.
  const assumptions = usePlanningAssumptions((s) => s.assumptions)
  const fetchAssumptions = usePlanningAssumptions((s) => s.fetch)
  const patchDebounced = usePlanningAssumptions((s) => s.patchDebounced)
  const hydratedRef = useRef(false)

  // Helper: build the full property_plans list from local Farm Avenue
  // slider state and patch it. Today the seed has only Farm Avenue, so
  // a list of length 0 or 1 is sufficient. Multi-property editing would
  // need to merge with the existing list rather than replace.
  const writeFarmAvenuePlan = useCallback((args: { sell?: boolean; year?: number; proceeds?: number } = {}) => {
    const sell = args.sell ?? sellFarmAvenue
    const year = args.year ?? farmAvenueSaleYear
    const proceeds = args.proceeds ?? farmAvenueNetProceeds
    patchDebounced({
      property_plans: sell
        ? [{
            holding_id: 'farm_avenue',
            label: '35 Farm Avenue',
            sell_year: year,
            net_proceeds_gbp: proceeds,
            include_in_fire: false,
          }]
        : [],
    })
  }, [sellFarmAvenue, farmAvenueSaleYear, farmAvenueNetProceeds, patchDebounced])

  useEffect(() => {
    if (!assumptions) {
      void fetchAssumptions()
      return
    }
    if (!hydratedRef.current) {
      hydratedRef.current = true
      setSpend(assumptions.target_drawdown_gbp ?? 220_000)
      setSwr(assumptions.swr_pct)
      setAge(assumptions.current_age)
      setRetireAge(assumptions.retirement_age)
      setSavings(assumptions.annual_savings_gbp)
      setRealReturn(assumptions.mean_real_return_pct)
      setIncludeSP(assumptions.include_state_pension)
    }
  }, [assumptions, fetchAssumptions])

  const fetchStatus = async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, any> = {
        annual_spend_gbp: spend,
        swr_pct: swr,
        current_age: age,
        retirement_age: retireAge,
        annual_savings_gbp: savings,
        real_return_pct: realReturn,
        include_state_pension: includeSP,
        include_external_cash: externalCash,
        property_value_gbp: propertyValue,
        other_assets_gbp: otherAssets,
        property_contribution_pct: propertyContributionPct,
        property_real_growth_pct: propertyRealGrowth,
        sell_property_pct: sellPropertyPct,
      }
      if (sellPropertyInYears !== '' && sellPropertyInYears >= 0) {
        params.sell_property_at_age = age + Number(sellPropertyInYears)
      }
      const res = await api.get<FireStatus>('/api/planning/fire', { params })
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load FIRE status')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch when inputs change (debounced via short timeout)
  useEffect(() => {
    const t = setTimeout(fetchStatus, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spend, swr, age, retireAge, savings, realReturn, includeSP, externalCash, propertyValue, otherAssets, propertyContributionPct, propertyRealGrowth, sellPropertyInYears, sellPropertyPct])

  const chartData = useMemo(() => {
    if (!data?.projection) return []
    return data.projection.map(p => ({
      ...p,
      age: age + p.year,
    }))
  }, [data, age])

  // Build-up to retirement: filters projection to only the accumulation phase
  // so Simon can see "here's how the pot grows from today until the day I retire".
  // Includes the target FIRE line (spend / swr%) for a clear gap visualisation.
  const buildUpData = useMemo(() => {
    if (!data?.projection) return []
    const yearsToRetire = Math.max(0, retireAge - age)
    return data.projection
      .slice(0, yearsToRetire + 1)
      .map((p) => ({
        ...p,
        age: age + p.year,
        gap:
          data.target_required_gbp > 0
            ? Math.max(0, data.target_required_gbp - p.value)
            : 0,
      }))
  }, [data, age, retireAge])

  const yearsToRetire = Math.max(0, retireAge - age)
  const projectedAtRetirement =
    buildUpData.length > 0 ? buildUpData[buildUpData.length - 1].value : 0
  const hitTargetByRetirement = data
    ? projectedAtRetirement >= data.target_required_gbp
    : false
  const gapAtRetirement = data
    ? Math.max(0, data.target_required_gbp - projectedAtRetirement)
    : 0
  const surplusAtRetirement = data
    ? Math.max(0, projectedAtRetirement - data.target_required_gbp)
    : 0

  const tierColour = data ? (TIER_COLOURS[data.classification.tier] ?? TIER_COLOURS.ACCUMULATING) : ''

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="max-w-3xl">
          <h1 className="page-title flex items-center gap-2">
            <Target size={20} className="text-blue-light" aria-hidden />
            Financial Planning &amp; FIRE
          </h1>
          <p className="text-sm text-text-secondary">
            Can we afford to stop working? When?
          </p>
          <p className="text-xs text-text-muted mt-2 leading-relaxed">
            This page projects your pot forward from <strong>today</strong> to your
            retirement age. While you're still working the pot <strong>accumulates</strong>{' '}
            (savings + growth, no withdrawals). From retirement age onwards it{' '}
            <strong>drawsdown</strong> your annual spend. The target is a lump sum big
            enough to support that spend at your chosen Safe Withdrawal Rate. If you
            <strong> sell property in N years</strong> (e.g. to move abroad), use the
            control in Assumptions to inject the equity into the liquid pot on that
            year — everything on the page re-runs.
          </p>
        </div>
        <button type="button" onClick={fetchStatus} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {/* Net worth split */}
      {data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
          <div
            className="stat-card"
            title="ISAs, SIPPs, GIAs, crypto — everything held in a broker or exchange. Excludes property and external cash."
          >
            <p className="stat-label">Tracked Investments</p>
            <p className="stat-value">{fmtGBP(data.tracked_total_gbp)}</p>
            <p className="text-xs text-text-muted">brokers + crypto + bonds</p>
          </div>
          <div
            className="stat-card"
            title="The pot the safe withdrawal rate (SWR) is calculated against. Tracked investments + external cash + any property equity you've marked as liquid."
          >
            <p className="stat-label">FIRE-Applicable</p>
            <p className="stat-value text-blue-light">{fmtGBP(data.fire_applicable_gbp)}</p>
            <p className="text-xs text-text-muted">drives the SWR maths</p>
          </div>
          <div
            className="stat-card"
            title="Primary residence + BTLs + art/business equity. Counts toward net worth but can't be drawn down without selling. Use the 'sell property at age' control below to model a downsize."
          >
            <p className="stat-label">Property + Other</p>
            <p className="stat-value text-text-secondary">{fmtGBP(data.illiquid_assets_gbp)}</p>
            <p className="text-xs text-text-muted">net worth only — not drawdownable</p>
          </div>
          <div
            className="stat-card"
            title="Everything added up: tracked investments + external cash + property net equity + other assets. The number at the top of the Command Centre."
          >
            <p className="stat-label">Total Net Worth</p>
            <p className="stat-value text-emerald-light">{fmtGBP(data.total_net_worth_gbp)}</p>
            <p className="text-xs text-text-muted">including everything</p>
          </div>
        </div>
      )}

      {/* Headline */}
      {data && (
        <div className={clsx('card p-6 border-2', tierColour)}>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="panel-title opacity-80">
                {data.classification.tier}
              </p>
              {TIER_EXPLAINERS[data.classification.tier] && (
                <p className="text-xs text-text-secondary mt-0.5">
                  {TIER_EXPLAINERS[data.classification.tier]}
                </p>
              )}
              <h2 className="text-2xl font-bold text-text-primary mt-1">{data.headline}</h2>
              <p className="text-sm text-text-secondary mt-2">{data.classification.label}</p>
              <div className="flex items-center gap-6 mt-4 flex-wrap text-sm">
                <div title="Today's liquid + SWR-applicable wealth">
                  <span className="text-text-muted">Today: </span>
                  <span className="font-bold text-text-primary">{fmtGBP(data.household_total_gbp)}</span>
                </div>
                <div title={`At age ${retireAge} after saving £${savings.toLocaleString()}/yr and ${realReturn}% real growth — withdrawals only start in retirement`}>
                  <span className="text-text-muted">At age {retireAge}: </span>
                  <span className="font-bold text-text-primary">{fmtGBP(data.value_at_retirement_gbp)}</span>
                </div>
                <div title={`Lump sum needed to sustain £${spend.toLocaleString()}/yr at a ${swr}% safe withdrawal rate`}>
                  <span className="text-text-muted">Required: </span>
                  <span className="font-bold text-text-primary">{fmtGBP(data.target_required_gbp)}</span>
                </div>
                <div>
                  {data.retirement_ready ? (
                    <>
                      <span className="text-text-muted" title={`At age ${retireAge} you'll have more than you need — this is the buffer`}>Surplus: </span>
                      <span className="font-bold text-emerald">+{fmtGBP(data.surplus_gbp)}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-text-muted" title={`Gap at age ${retireAge} between projected pot and what the SWR maths needs`}>Shortfall: </span>
                      <span className="font-bold text-rose-light">−{fmtGBP(data.shortfall_gbp)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="text-center shrink-0">
              <div className="stat-hero text-gradient">
                {data.current_multiple}<span className="text-2xl text-text-muted">×</span>
              </div>
              <p className="text-xs text-text-muted mt-1">annual spend</p>
            </div>
          </div>

          {/* Show your working — formula breakdown */}
          <details className="mt-5 group">
            <summary className="text-xs text-text-muted cursor-pointer hover:text-text-primary select-none inline-flex items-center gap-1">
              <span className="group-open:rotate-90 transition-transform inline-block">▸</span>
              Show your working
            </summary>
            <div className="mt-3 p-4 rounded-lg bg-black/20 border border-border text-xs font-mono space-y-2 text-text-secondary">
              <p className="text-text-muted uppercase text-[10px] tracking-wider mb-1">
                1. Today's FIRE pot
              </p>
              <p>
                Tracked investments ({fmtGBP(data.tracked_total_gbp)})
                {externalCash > 0 && ` + external cash (${fmtGBP(externalCash)})`}
                {data.property_fire_contribution_gbp && data.property_fire_contribution_gbp > 0 &&
                  ` + property equity counted as liquid (${fmtGBP(data.property_fire_contribution_gbp)})`}
                {' = '}
                <strong className="text-text-primary">{fmtGBP(data.household_total_gbp)}</strong>
              </p>

              <p className="text-text-muted uppercase text-[10px] tracking-wider mb-1 mt-3">
                2. Projection to age {retireAge} ({Math.max(0, retireAge - age)} years away)
              </p>
              <p>
                Grow at {realReturn}%/yr real, add £{savings.toLocaleString()}/yr savings, no drawdown yet
                {sellPropertyInYears !== '' && sellPropertyPct > 0 &&
                  ` → sell ${sellPropertyPct}% of property in year ${sellPropertyInYears}`}
              </p>
              <p>
                = <strong className="text-text-primary">{fmtGBP(data.value_at_retirement_gbp)}</strong> at age {retireAge}
              </p>

              <p className="text-text-muted uppercase text-[10px] tracking-wider mb-1 mt-3">
                3. How much do you need at retirement?
              </p>
              <p>
                Annual spend £{spend.toLocaleString()} ÷ SWR {swr}%
                {' = '}
                <strong className="text-text-primary">{fmtGBP(data.target_required_gbp)}</strong>
              </p>
              {includeSP && data.target_with_state_pension_gbp !== data.target_required_gbp && (
                <p className="text-text-muted">
                  After state pension at 67: {fmtGBP(data.target_with_state_pension_gbp)}
                </p>
              )}

              <p className="text-text-muted uppercase text-[10px] tracking-wider mb-1 mt-3">
                4. Gap at retirement
              </p>
              <p>
                {fmtGBP(data.value_at_retirement_gbp)} (projected) − {fmtGBP(data.target_required_gbp)} (needed)
                {' = '}
                <strong className={data.retirement_ready ? 'text-emerald' : 'text-rose-light'}>
                  {data.retirement_ready ? '+' : '−'}{fmtGBP(data.retirement_ready ? data.surplus_gbp : data.shortfall_gbp)}
                </strong>
              </p>

              <p className="text-[10px] text-text-muted mt-4 font-sans">
                All figures in <strong>today's money</strong> (real terms, net of inflation).
                Property tracked separately: £{data.property_net_equity_gbp?.toLocaleString() ?? 0} net equity
                {data.property_at_retirement_gbp ? ` growing to ${fmtGBP(data.property_at_retirement_gbp)} by age ${retireAge}` : ''}.
              </p>
            </div>
          </details>
        </div>
      )}

      {/* Inputs */}
      <div className="card p-5">
        <h2 className="panel-title mb-2 flex items-center gap-2">
          <Sparkles size={14} className="text-blue-light" aria-hidden />
          Assumptions
        </h2>
        <p className="text-xs text-text-muted mb-4">
          Projection is in <strong>real terms</strong> — "real return" and "real property
          growth" are already net of inflation. Contributions only happen while you're
          still working; withdrawals only start at your retirement age.
          Set <em>Sell property at age</em> to model downsizing — the equity gets injected
          into the liquid pot that year.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <EditableNumberSlider
            label="Annual spend (gross)"
            value={spend}
            onChange={(v) => { setSpend(v); patchDebounced({ target_drawdown_gbp: v }) }}
            min={20_000}
            max={400_000}
            step={5_000}
            format={fmtGBP}
            help="How much the household needs to draw from the portfolio each year in retirement, gross (pre-tax). Exclude spending covered by state/DB pensions — those are added back separately. Type an exact figure in the box to go beyond the slider range."
          />
          <NumberSlider
            label="Safe Withdrawal Rate"
            value={swr}
            onChange={(v) => { setSwr(v); patchDebounced({ swr_pct: v }) }}
            min={2.5}
            max={5.0}
            step={0.25}
            format={(v) => `${v.toFixed(2)}%`}
            help="The % of the portfolio you can draw each year without running out. Trinity study uses 4%; modern conservative planners use 3.5% or 3%. Lower = safer but needs a bigger pot."
          />
          <NumberSlider
            label="Current age"
            value={age}
            onChange={(v) => { setAge(v); patchDebounced({ current_age: v }) }}
            min={25}
            max={85}
            step={1}
            format={(v) => `${v}`}
            help="Your age today — drives the horizon for growth projection. Set to the older partner's age if you plan for them."
          />
          <NumberSlider
            label="Retirement age"
            value={retireAge}
            onChange={(v) => { setRetireAge(v); patchDebounced({ retirement_age: v }) }}
            min={age}
            max={85}
            step={1}
            format={(v) => `${v}`}
            help="The age you stop working. Between current and retirement age the projection ADDS annual savings and does NOT draw anything. From retirement age onward it draws the annual spend."
          />
          <NumberSlider
            label="Annual savings"
            value={savings}
            onChange={(v) => { setSavings(v); patchDebounced({ annual_savings_gbp: v }) }}
            min={0}
            max={100_000}
            step={1_000}
            format={fmtGBP}
            help="Net amount added to the pot each year while still working (e.g. two ISA allowances = £40k). Only applies during the working phase; switches to zero at retirement age."
          />
          <NumberSlider
            label="Real return p.a."
            value={realReturn}
            onChange={(v) => { setRealReturn(v); patchDebounced({ mean_real_return_pct: v }) }}
            min={1}
            max={10}
            step={0.5}
            format={(v) => `${v.toFixed(1)}%`}
            help="Expected annual portfolio return AFTER inflation. Historical 60/40 real return is ~5%. Lower = more conservative. The whole projection is in today's money."
          />
          <NumberSlider
            label="External savings (high-street)"
            value={externalCash}
            onChange={setExternalCash}
            min={0}
            max={500_000}
            step={5_000}
            format={fmtGBP}
            help="Cash held in bank/savings accounts outside the platform (Chase, Marcus, etc). Counts toward the FIRE pot but doesn't appear in /accounts."
          />
          <NumberSlider
            label="Property value override (£)"
            value={propertyValue}
            onChange={setPropertyValue}
            min={0}
            max={3_000_000}
            step={25_000}
            format={fmtGBP}
            help="Leave at 0 to use the Property account's real net equity. Set a value here only if you want to override the stored property valuation for a what-if."
          />
          <NumberSlider
            label="% of property equity in FIRE pot"
            value={propertyContributionPct}
            onChange={setPropertyContributionPct}
            min={0}
            max={100}
            step={5}
            format={(v) => `${v.toFixed(0)}%`}
            help="0% = property counts toward net worth only (default — you can't drawdown from a house). 100% = you've sold everything and it's all liquid. Useful for modelling downsize scenarios."
          />
          <NumberSlider
            label="Real property growth p.a."
            value={propertyRealGrowth}
            onChange={setPropertyRealGrowth}
            min={-1}
            max={5}
            step={0.25}
            format={(v) => `${v.toFixed(2)}%`}
            help="UK house price growth ABOVE inflation. Long-run average is 1-1.5% real. Set to 0 for a flat scenario, or negative to model a housing downturn."
          />
          <div
            className="col-span-2 sm:col-span-2 lg:col-span-2 flex flex-col gap-2"
            title="Model selling the property and moving the equity into the liquid pot N years from today. At 100% the property disappears from the projection and the liquid balance jumps on that year. Great for the 'sell everything and move abroad in 3 years' scenario."
          >
            <div className="flex items-center gap-2">
              <label htmlFor="sell-years" className="text-[10px] uppercase tracking-wider text-text-muted">
                Sell property in
              </label>
              <span className="text-[9px] text-text-muted/60 cursor-help">?</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                id="sell-years"
                type="number"
                value={sellPropertyInYears === '' ? '' : sellPropertyInYears}
                onChange={(e) => {
                  const v = e.target.value
                  setSellPropertyInYears(v === '' ? '' : Math.max(0, parseInt(v, 10) || 0))
                }}
                placeholder="never"
                min={0}
                max={40}
                className="input text-sm w-20"
                aria-label="Sell property in N years"
              />
              <span className="text-[10px] text-text-muted shrink-0">years</span>
              <span className="text-[10px] text-text-muted shrink-0">·</span>
              <input
                type="number"
                value={sellPropertyPct}
                onChange={(e) => setSellPropertyPct(Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)))}
                min={0}
                max={100}
                step={5}
                className="input text-sm w-16"
                aria-label="Percentage of property to sell"
                disabled={sellPropertyInYears === ''}
              />
              <span className="text-[10px] text-text-muted shrink-0">% sold</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                type="button"
                onClick={() => { setSellPropertyInYears(3); setSellPropertyPct(100) }}
                className={clsx(
                  'text-[10px] px-2 py-0.5 rounded border transition-colors',
                  sellPropertyInYears === 3 && sellPropertyPct === 100
                    ? 'bg-blue/20 border-blue/40 text-blue-light'
                    : 'border-border text-text-muted hover:text-text-primary hover:bg-surface-hover',
                )}
              >
                Sell in 3y (move abroad)
              </button>
              <button
                type="button"
                onClick={() => { setSellPropertyInYears(5); setSellPropertyPct(100) }}
                className={clsx(
                  'text-[10px] px-2 py-0.5 rounded border transition-colors',
                  sellPropertyInYears === 5 && sellPropertyPct === 100
                    ? 'bg-blue/20 border-blue/40 text-blue-light'
                    : 'border-border text-text-muted hover:text-text-primary hover:bg-surface-hover',
                )}
              >
                Sell in 5y
              </button>
              <button
                type="button"
                onClick={() => { setSellPropertyInYears(10); setSellPropertyPct(50) }}
                className={clsx(
                  'text-[10px] px-2 py-0.5 rounded border transition-colors',
                  sellPropertyInYears === 10 && sellPropertyPct === 50
                    ? 'bg-blue/20 border-blue/40 text-blue-light'
                    : 'border-border text-text-muted hover:text-text-primary hover:bg-surface-hover',
                )}
              >
                Downsize in 10y
              </button>
              {sellPropertyInYears !== '' && (
                <button
                  type="button"
                  onClick={() => setSellPropertyInYears('')}
                  className="text-[10px] px-2 py-0.5 text-text-muted hover:text-rose-light"
                >
                  never
                </button>
              )}
            </div>
            {sellPropertyInYears !== '' && (
              <p className="text-[10px] text-text-muted leading-tight">
                Property injected into liquid pot in year {sellPropertyInYears} (age {age + Number(sellPropertyInYears)}).
                {sellPropertyPct < 100 && ` ${sellPropertyPct}% sold — remainder keeps growing.`}
              </p>
            )}
          </div>
          {/* ── 35 Farm Avenue sale scenario ──────────────────────────── */}
          <div className="space-y-2 p-3 rounded border border-border bg-surface-card/50">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sellFarmAvenue"
                checked={sellFarmAvenue}
                onChange={e => {
                  setSellFarmAvenue(e.target.checked)
                  writeFarmAvenuePlan({ sell: e.target.checked })
                }}
                className="rounded border-border"
              />
              <label htmlFor="sellFarmAvenue" className="text-xs font-medium">
                Sell 35 Farm Avenue
              </label>
              <span className="text-[10px] text-text-muted ml-auto">On market, vacant</span>
            </div>
            {sellFarmAvenue && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="farmAvenueSaleYear" className="text-[10px] text-text-muted">Sale year</label>
                  <input
                    id="farmAvenueSaleYear"
                    type="number"
                    value={farmAvenueSaleYear}
                    onChange={e => {
                      const v = parseInt(e.target.value) || 2026
                      setFarmAvenueSaleYear(v)
                      writeFarmAvenuePlan({ year: v })
                    }}
                    min={2024}
                    max={2040}
                    className="input text-sm w-full"
                    placeholder="2026"
                  />
                </div>
                <div>
                  <label htmlFor="farmAvenueProceeds" className="text-[10px] text-text-muted">Net proceeds (£)</label>
                  <input
                    id="farmAvenueProceeds"
                    type="number"
                    value={farmAvenueNetProceeds}
                    onChange={e => {
                      const v = parseFloat(e.target.value) || 0
                      setFarmAvenueNetProceeds(v)
                      writeFarmAvenuePlan({ proceeds: v })
                    }}
                    min={0}
                    max={1_000_000}
                    step={1000}
                    className="input text-sm w-full"
                    placeholder="310770"
                  />
                </div>
              </div>
            )}
            {sellFarmAvenue && (
              <p className="text-[10px] text-text-muted leading-tight">
                £{farmAvenueNetProceeds.toLocaleString()} net after mortgage + CGT + costs.
                Flows into FIRE projection and Monte Carlo automatically.
              </p>
            )}
          </div>

          <NumberSlider
            label="Other assets (art, business, etc.)"
            value={otherAssets}
            onChange={setOtherAssets}
            min={0}
            max={1_000_000}
            step={10_000}
            format={fmtGBP}
            help="Illiquid assets you want counted in total net worth but not in the drawdownable pot. Art, collectibles, private company equity, etc."
          />
          <label
            className="flex items-center gap-2 text-xs text-text-secondary self-end col-span-2 sm:col-span-1"
            title="When on, the full UK state pension (~£11,973/yr at 67) is subtracted from your annual drawdown need once you reach state pension age. Turn off if you want a pension-free target."
          >
            <input
              type="checkbox"
              checked={includeSP}
              onChange={(e) => { setIncludeSP(e.target.checked); patchDebounced({ include_state_pension: e.target.checked }) }}
              className="rounded border-border bg-surface-card"
            />
            Include UK state pension (£11,973/yr at 67)
          </label>
        </div>
      </div>

      {/* Multiples table */}
      {data && (
        <div className="card p-5">
          <h2 className="panel-title mb-3">SWR Sensitivity</h2>
          <p className="text-xs text-text-muted mb-4">
            Different "safe withdrawal rates" demand different portfolio sizes. The Trinity study uses 4%;
            modern conservative planners use 3.5% or even 3%. Pick the rate you can sleep with.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {data.multiples_table.map(m => (
              <div
                key={m.swr_pct}
                title={
                  `At a ${m.swr_pct}% safe withdrawal rate, you need £${(m.required_gbp ?? 0).toLocaleString()} today to sustain £${(data.annual_spend_gbp ?? 0).toLocaleString()}/yr spend. ` +
                  (m.achieved
                    ? `You have £${(m.surplus_gbp ?? 0).toLocaleString()} more than needed.`
                    : `You are £${(m.shortfall_gbp ?? 0).toLocaleString()} short today — see the main headline for the retirement-age view.`)
                }
                className={clsx(
                  'p-3 rounded-lg border text-center',
                  m.achieved
                    ? 'border-emerald/30 bg-emerald/5'
                    : 'border-rose/20 bg-rose/5',
                )}
              >
                <p className="text-[10px] uppercase tracking-wider text-text-muted">{fmtPct(m.swr_pct, 1)} SWR</p>
                <p className="text-sm font-bold text-text-primary mt-1">{fmtGBPCompact(m.required_gbp)}</p>
                <p className={clsx(
                  'text-[10px] mt-1 font-semibold',
                  m.achieved ? 'text-emerald-light' : 'text-rose-light',
                )}>
                  {m.achieved ? `+${fmtGBPCompact(m.surplus_gbp)}` : `−${fmtGBPCompact(m.shortfall_gbp)}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Build-Up to Retirement — pre-retirement accumulation phase only */}
      {data && buildUpData.length > 0 && (
        <div className="card p-5 border-2 border-blue/30">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <h2 className="panel-title flex items-center gap-2">
                <TrendingUp size={14} className="text-emerald-light" aria-hidden />
                Build-Up to Retirement
              </h2>
              <p className="text-xs text-text-muted mt-1">
                Accumulation phase from today (age {age}) to your chosen retirement date (age {retireAge}).
                You're still saving £{savings.toLocaleString('en-GB')}/yr plus compounding — the pot keeps
                growing against your {fmtGBP(data.target_required_gbp)} FIRE target.
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-text-muted">
                {yearsToRetire === 0 ? 'Retiring today' : `${yearsToRetire} years to go`}
              </p>
              {hitTargetByRetirement ? (
                <p className="text-lg font-bold text-emerald">
                  On track · +{fmtGBP(surplusAtRetirement)} surplus
                </p>
              ) : (
                <p className="text-lg font-bold text-rose-light">
                  Short by {fmtGBP(gapAtRetirement)}
                </p>
              )}
            </div>
          </div>

          {/* Headline KPIs: today / projected-at-retirement / target / gap */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="stat-card">
              <p className="stat-label">Today</p>
              <p className="stat-value">{fmtGBP(data.household_total_gbp)}</p>
              <p className="text-xs text-text-muted">current FIRE pot</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">At retirement (age {retireAge})</p>
              <p className={clsx(
                'stat-value',
                hitTargetByRetirement ? 'text-emerald-light' : 'text-amber-light',
              )}>
                {fmtGBP(projectedAtRetirement)}
              </p>
              <p className="text-xs text-text-muted">
                projected at {realReturn}% real return
              </p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Target (SWR {swr}%)</p>
              <p className="stat-value">{fmtGBP(data.target_required_gbp)}</p>
              <p className="text-xs text-text-muted">{fmtGBP(spend)}/yr ÷ {swr}%</p>
            </div>
            <div className="stat-card">
              <p className="stat-label">Annual savings needed</p>
              <p className="stat-value text-blue-light">
                {yearsToRetire > 0 && !hitTargetByRetirement
                  ? fmtGBP(Math.max(0, gapAtRetirement / yearsToRetire))
                  : '£0'}
              </p>
              <p className="text-xs text-text-muted">
                {hitTargetByRetirement ? 'already on track' : `over ${yearsToRetire} yrs to close gap`}
              </p>
            </div>
          </div>

          {/* Area chart: pot value climbing toward the target line */}
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={buildUpData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis
                dataKey="age"
                stroke={axisTickFill}
                tick={{ fontSize: 11 }}
                label={{ value: 'Age', position: 'insideBottom', offset: -5, fill: axisTickFill }}
              />
              <YAxis
                stroke={axisTickFill}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => fmtGBPCompact(v)}
              />
              <Tooltip
                contentStyle={tooltipContentStyle}
                formatter={(v: any, name: string) => [fmtGBP(v as number), name]}
                labelFormatter={(l) => `Age ${l}`}
              />
              <Area
                type="monotone"
                dataKey="value"
                name="Projected pot"
                stroke="#3b82f6"
                strokeWidth={2.5}
                fill="#3b82f6"
                fillOpacity={0.15}
              />
              <ReferenceLine
                y={data.target_required_gbp}
                stroke="#10b981"
                strokeDasharray="4 4"
                label={{
                  value: `FIRE target ${fmtGBPCompact(data.target_required_gbp)}`,
                  fill: '#10b981',
                  fontSize: 11,
                  position: 'right',
                }}
              />
              <ReferenceLine
                x={retireAge}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                label={{ value: 'Retire', fill: '#f59e0b', fontSize: 11 }}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Year-by-year table underneath */}
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-text-muted hover:text-text-secondary">
              Year-by-year breakdown ({buildUpData.length} years)
            </summary>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs table-pro">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Age</th>
                    <th className="!text-right">Pot value</th>
                    <th className="!text-right">Gap to target</th>
                    <th className="!text-right">% to target</th>
                  </tr>
                </thead>
                <tbody>
                  {buildUpData.map((row) => {
                    const pct = data.target_required_gbp > 0
                      ? Math.min(100, (row.value / data.target_required_gbp) * 100)
                      : 0
                    return (
                      <tr key={row.year}>
                        <td className="text-text-muted">{row.year}</td>
                        <td className="text-text-primary">{row.age}</td>
                        <td className="text-right font-mono text-text-primary">{fmtGBP(row.value)}</td>
                        <td className={clsx(
                          'text-right font-mono',
                          row.gap > 0 ? 'text-rose-light' : 'text-emerald-light',
                        )}>
                          {row.gap > 0 ? `−${fmtGBP(row.gap)}` : '✓'}
                        </td>
                        <td className="text-right font-mono text-text-secondary">
                          {pct.toFixed(0)}%
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}

      {/* Projection chart */}
      {chartData.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-1 flex items-center gap-2">
            <TrendingUp size={14} className="text-blue-light" aria-hidden />
            50-Year Projection (real terms)
          </h2>
          <p className="text-xs text-text-muted mb-4">
            Portfolio value over time assuming {realReturn}% real return,
            withdrawing {fmtGBP(spend)}/yr. State pension kicks in at age {data?.assumptions?.state_pension_age}.
          </p>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis
                dataKey="age"
                stroke={axisTickFill}
                tick={{ fontSize: 11 }}
                label={{ value: 'Age', position: 'insideBottom', offset: -5, fill: axisTickFill }}
              />
              <YAxis
                stroke={axisTickFill}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => fmtGBPCompact(v)}
              />
              <Tooltip
                contentStyle={tooltipContentStyle}
                formatter={(v: any) => fmtGBP(v as number)}
                labelFormatter={(l) => `Age ${l}`}
              />
              <ReferenceLine
                x={retireAge}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                label={{ value: 'Retire', fill: '#f59e0b', fontSize: 11 }}
              />
              {data && data.target_required_gbp > 0 && (
                <ReferenceLine
                  y={data.target_required_gbp}
                  stroke="#10b981"
                  strokeDasharray="3 3"
                  label={{ value: 'FIRE', fill: '#10b981', fontSize: 11, position: 'right' }}
                />
              )}
              <Line
                type="monotone"
                dataKey="value"
                name="Portfolio"
                stroke="#3b82f6"
                strokeWidth={2.5}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Sequence-of-returns risk */}
      {data && (
        <div className="card p-5 border-amber/20 bg-amber/5">
          <h2 className="panel-title mb-2 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber" aria-hidden />
            Sequence-of-Returns Risk
            <span
              className={clsx(
                'badge text-[10px]',
                data.sequence_risk.level === 'LOW' && 'badge-green',
                data.sequence_risk.level === 'MODERATE' && 'badge-amber',
                data.sequence_risk.level === 'HIGH' && 'badge-red',
              )}
            >
              {data.sequence_risk.level}
            </span>
          </h2>
          <p className="text-xs text-text-secondary">{data.sequence_risk.explanation}</p>
        </div>
      )}

      {/* Years-to-FIRE summary */}
      {data && data.years_to_fire !== null && data.years_to_fire > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <Calendar size={14} className="text-blue-light" aria-hidden />
            Time to FIRE
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-text-muted text-xs">Without state pension</p>
              <p className="text-2xl font-bold text-text-primary mt-1">
                {fmtNum(data.years_to_fire, 1)} years
              </p>
              <p className="text-xs text-text-muted">
                Hits FIRE at age {fmtNum(data.fire_age, 1)}
              </p>
            </div>
            {includeSP && data.years_to_fire_with_state_pension !== null && (
              <div>
                <p className="text-text-muted text-xs">With state pension overlay</p>
                <p className="text-2xl font-bold text-emerald-light mt-1">
                  {fmtNum(data.years_to_fire_with_state_pension, 1)} years
                </p>
                <p className="text-xs text-text-muted">
                  Hits FIRE at age {fmtNum(data.fire_age_with_state_pension, 1)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Methodology footer */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          Methodology &amp; FIRE definitions
        </summary>
        <div className="mt-3 space-y-2">
          <p><span className="text-text-primary font-semibold">FAT FIRE</span> — 33×+ multiple, 3% withdrawal. Bulletproof, retire anywhere.</p>
          <p><span className="text-text-primary font-semibold">Standard FIRE</span> — 28.5×+, 3.5% withdrawal. Modern conservative target.</p>
          <p><span className="text-text-primary font-semibold">Lean / Classic FIRE</span> — 25×, the 4% Trinity rule.</p>
          <p><span className="text-text-primary font-semibold">Barista FIRE</span> — 20×, covers most expenses, light part-time work bridges the gap.</p>
          <p><span className="text-text-primary font-semibold">Coast FIRE</span> — 12.5×, can stop saving and let compound do the rest.</p>
          <p className="pt-2 text-text-muted">
            Real-return assumption: portfolio growth net of inflation. The 50-year projection is in
            today's pounds. Excludes external bank/savings cash unless added via the input above.
            Manager-controlled cash sitting in non-discretionary accounts still counts toward your
            net worth — it just isn't actively producing returns.
          </p>
          <p className="text-text-muted">
            <span className="text-text-primary font-semibold">Property treatment:</span> primary
            residence and other illiquid assets show in <em>Total Net Worth</em> but are deliberately
            excluded from <em>FIRE-Applicable</em> and the SWR multiples. You can't withdraw 4% from
            a house each year. If you'd downsize / equity-release, model it by adding the freed-up
            equity into "External savings" instead.
          </p>
        </div>
      </details>
      <ScenarioDisclaimerFooter />
    </div>
  )
}

// ── Slider input component ───────────────────────────────────────────────────
function NumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  format,
  help,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
  help?: string
}) {
  return (
    <div title={help}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1">
          {label}
          {help && (
            <span
              className="text-[9px] text-text-muted/60 cursor-help"
              aria-label={`Help: ${help}`}
            >
              ?
            </span>
          )}
        </label>
        <span className="text-xs font-bold text-text-primary">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
        title={help}
      />
    </div>
  )
}
